import { AuthProvider } from "@refinedev/core";
import {
  User as FirebaseUser,
  getIdTokenResult,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import _ from "lodash";

import { auth, defaultDb, providers } from "@/firebaseConfig";
import { Sentry } from "@/lib/sentry";

export interface ILoginArgs {
  email?: string;
  password?: string;
  provider: "google" | "email";
}

export type IIdentity = {
  id: string;
  displayName: string;
  email: string;
  photoUrl: string;
  adminMetadata: any;
};

export const authProvider: AuthProvider = {
  getIdentity: async () => {
    if (auth?.currentUser?.uid) {
      const docRef = doc(defaultDb, "users", auth?.currentUser?.uid);
      const snapshot = await getDoc(docRef);

      if (snapshot.exists()) {
        Sentry.setUser({
          id: auth.currentUser.uid,
          email: auth.currentUser.email ?? undefined,
          username: snapshot.data().displayName ?? snapshot.data().display_name,
        });
        return {
          ..._.mapKeys(snapshot.data(), (_value, key) => _.camelCase(key)),
          id: auth?.currentUser?.uid,
          avatar: snapshot.data().iconUrl,
        };
      }
    }

    return null;
  },
  login: async ({ email, password, provider }: ILoginArgs) => {
    Sentry.addBreadcrumb({
      category: "auth",
      message: `Login attempt via ${provider}`,
      level: "info",
    });
    try {
      let userCredential;
      switch (provider) {
        case "email":
          userCredential = await signInWithEmailAndPassword(
            auth,
            email!,
            password!,
          );
          break;
        case "google":
          userCredential = await signInWithPopup(auth, providers.google);
          break;
      }
      const userToken = await userCredential?.user?.getIdToken?.();
      const userTokenResult = await userCredential?.user?.getIdTokenResult?.();
      if (userToken && !!userTokenResult.claims.admin) {
        Sentry.addBreadcrumb({
          category: "auth",
          message: "Login successful",
          level: "info",
          data: { uid: userCredential?.user?.uid },
        });
        return {
          success: true,
          redirectTo: "/events",
        };
      } else {
        Sentry.addBreadcrumb({
          category: "auth",
          message: "Login rejected — not an admin",
          level: "warning",
        });
        return Promise.resolve({
          success: false,
          error: { name: "Login Error", message: "User invalid" },
        });
      }
    } catch (error) {
      Sentry.addBreadcrumb({
        category: "auth",
        message: "Login failed with exception",
        level: "error",
        data: { error: (error as any)?.message },
      });
      return Promise.resolve({
        success: false,
        error: {
          name: "Login Error",
          message: (error as any)?.message || "Unknown error",
        },
      });
    }
  },
  check: async () => {
    const user = await getFirebaseUser();
    if (user) {
      const idTokenResult = await getIdTokenResult(user);
      if (idTokenResult.claims.admin) {
        return {
          authenticated: true,
        };
      }
    }
    Sentry.addBreadcrumb({
      category: "auth",
      message: "Auth check failed — unauthenticated",
      level: "warning",
    });
    return {
      authenticated: false,
      error: {
        name: "Check error",
        message: "User invalid",
      },
    };
  },
  logout: async () => {
    Sentry.addBreadcrumb({
      category: "auth",
      message: "User logged out",
      level: "info",
    });
    Sentry.setUser(null);
    await signOut(auth);
    return {
      success: true,
      redirectTo: "/login",
    };
  },
  getPermissions: async (): Promise<any> => {
    if (auth?.currentUser) {
      const idTokenResult = await getIdTokenResult(auth.currentUser);
      return idTokenResult?.claims;
    } else {
      return Promise.resolve([]);
    }
  },
  forgotPassword: async (email: string) => {
    if (auth) {
      await sendPasswordResetEmail(auth, email);
      return {
        success: true,
      };
    } else {
      return {
        success: false,
        error: {
          name: "Forgot Password Error",
          message: "User invalid",
        },
      };
    }
  },
  onError: async (error) => {
    console.error(error);
    return { error };
  },
  // public handleResetPassword(email: string) {
  //   return sendPasswordResetEmail(this.auth, email);
  // }
  // public async onUpdateUserData(args: IRegisterArgs) {
  //   try {
  //     if (this.auth?.currentUser) {
  //       const { displayName, email, password } = args;
  //       if (password) {
  //         await updatePassword(this.auth.currentUser, password);
  //       }
  //       if (email && this.auth.currentUser.email !== email) {
  //         await updateEmail(this.auth.currentUser, email);
  //       }
  //       if (displayName && this.auth.currentUser.displayName !== displayName) {
  //         await updateProfile(this.auth.currentUser, {
  //           displayName: displayName,
  //         });
  //       }
  //     }
  //   } catch (error) {
  //     return Promise.reject(error);
  //   }
};

export const getFirebaseUser = (): Promise<FirebaseUser> => {
  return new Promise<FirebaseUser>((resolve, reject) => {
    const unsubscribe = auth?.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user as FirebaseUser | PromiseLike<FirebaseUser>);
    }, reject);
  });
};
